import React, { useContext } from "react";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";

import { RetornoLoteContext } from "../../Context/RetornoLoteContext";
import SelectSituacao from "../SelectSituacao";
import InputMotivo from "../InputMotivo";
import InputValorRecebido from "../InputValorRecebido";
import InputJustificativa from "../InputJustificativa";

export default function GlosaItem({ item, index, disabled = false }) {
  const { setGlosas, setAtualizaValoresRecebimentoManual } =
    useContext(RetornoLoteContext);

  const handleDelete = () => {
    setGlosas((prevState) => {
      const updatedGlosas = prevState.filter((glosa) => glosa.id !== item.id);
      return updatedGlosas;
    });
    setAtualizaValoresRecebimentoManual((prevState) => !prevState);
  };

  const getSituacaoConfig = () => {
    return {
      color: "#FCFCFC", // cinza muito claro
      borderColor: "#e0e0e0", // cinza suave
      textColor: "#757575", // cinza escuro
    };
  };

  const getSituacaoMap = (situacao) => {
    const situacaoMap = {
      EmAnalise: "Em Análise",
      Analise: "Em Recurso",
      Glosado: "Glosado",
      Acatado: "Glosado Acatado",
    };

    return situacaoMap[situacao] || situacao;
  };

  const isItemComplete =
    item.situacao === "EmAnalise" ||
    (item.situacao === "Glosado" && item.valor_recebido) ||
    (item.situacao === "Acatado" && item.motivo && item.valor_recebido) ||
    (item.situacao !== "EmAnalise" &&
      item.situacao !== "Acatado" &&
      item.motivo &&
      item.valor_recebido);

  const situacaoConfig = getSituacaoConfig();

  return (
    <Card
      sx={{
        mb: 2,
        border: `1px solid ${situacaoConfig.borderColor}`,
        backgroundColor: situacaoConfig.color,
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isItemComplete ? (
                <CheckCircleIcon
                  sx={{ color: situacaoConfig.textColor, fontSize: 24 }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{ color: "#bdbdbd", fontSize: 24 }}
                />
              )}
              <Typography
                variant="h6"
                sx={{
                  color: situacaoConfig.textColor,
                  fontWeight: 600,
                }}
              >
                Item #{index + 1}
              </Typography>
            </Box>

            {item.situacao && (
              <Chip
                label={getSituacaoMap(item.situacao)}
                size="small"
                sx={{
                  backgroundColor: situacaoConfig.textColor,
                  color: "white",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {item.valor_recebido && (
              <Typography
                variant="body2"
                sx={{
                  color: situacaoConfig.textColor,
                  fontWeight: 500,
                  backgroundColor: "rgba(255,255,255,0.7)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                R${" "}
                {Number(item.valor_recebido).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}

            {!disabled && (
              <Tooltip title="Remover item">
                <IconButton
                  onClick={handleDelete}
                  sx={{
                    color: "#f44336",
                    backgroundColor: "rgba(255,255,255,0.8)",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 54, 0.1)",
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={3}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  color: situacaoConfig.textColor,
                  fontWeight: 600,
                }}
              >
                Situação
              </Typography>
              <Box>
                <SelectSituacao row={item} />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={7}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  color: situacaoConfig.textColor,
                  fontWeight: 600,
                }}
              >
                Motivo
              </Typography>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  borderRadius: 1,
                  border: `1px solid ${situacaoConfig.borderColor}`,
                }}
              >
                <InputMotivo
                  row={item}
                  disabled={item.situacao === "EmAnalise"}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  color: situacaoConfig.textColor,
                  fontWeight: 600,
                }}
              >
                Valor (R$)
              </Typography>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  borderRadius: 1,
                  border: `1px solid ${situacaoConfig.borderColor}`,
                }}
              >
                <InputValorRecebido row={item} />
              </Box>
            </Box>
          </Grid>

          {item.situacao === "Acatado" && (
            <Grid item xs={12} md={12}>
              <InputJustificativa
                justificativa={item.justificativa || ""}
                id={item.id}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
